<template>
  <div class="field-group-container mb-3">
    <label>
      <question-display :question="question" customClass="customHeader"></question-display>
    </label>
    <br />

    <div v-for="field in question.fields" :key="field.field_name" class="mb-3 labelSpacing">
      <label>{{ field.field_name }}</label>

      <div v-if="field.type === 'number'">
        <input
          type="number"
          v-model.number="inputValues[field.field_name]"
          class="form-control mb-3"
          @input="updateTotal($event, field)"
          :placeholder="field.placeholder"
        />
      </div>

      <div v-if="field.type === 'money-input'">
        <input
          type="text"
          :value="formattedMoneyValues[field.field_name]"
          :placeholder="field.placeholder || ''"
          class="form-control"
          @input="onMoneyInput($event, field.field_name)"
        />
      </div>

      <div v-if="field.type === 'radio'">
        <div v-for="option in field.options" :key="`${field.field_name}-${option}`" class="checkWidth">
          <label class="curved-checkbox">
            <input
              type="radio"
              :value="option"
              v-model="inputValues[field.field_name]"
              @change="updateInput"
              class="form-check-input"
            />&nbsp;&nbsp;{{ option }}
          </label>
        </div>
      </div>

      <div v-if="field.type === 'dropdown'">
        <select
          v-model="inputValues[field.field_name]"
          class="form-control form-select"
          @change="updateInput"
        >
          <option value="" disabled>Select an option</option>
          <option
            v-for="option in dropdownOptions[field.field_name]"
            :key="option.value"
            :value="option.value"
          >
            {{ option.name }}
          </option>
        </select>
      </div>

      <div v-if="field.type === 'dropdown-search'">
        <multiselect
          v-model="inputValues[field.field_name]"
          :options="dropdownOptions[field.field_name] || []"
          :searchable="true"
          :close-on-select="true"
          :clear-on-select="false"
          label="name"
          track-by="value"
          @update:modelValue="updateInput"
        ></multiselect>
      </div>

      <div v-if="field.type === 'number-minutes-hour'">
        <div class="form-inline">
          <input
            type="number"
            v-model.number="inputValues[field.field_name].value"
            :placeholder="'value'"
            class="form-control i1"
            min="0"
            @input="updateInput"
          />
          <select
            v-model="inputValues[field.field_name].unit"
            class="form-control form-select i2"
            @change="updateInput"
          >
            <option value="minutes">Minutes</option>
            <option value="hour">Hours</option>
          </select>
        </div>
      </div>

      <div v-if="field.type === 'display-number'" class="form-control mb-3 dis-number">
        {{ inputValues[field.field_name] || 0 }}
      </div>
    </div>

    <small v-if="showValidation && question.required && !isFormValid" class="text-danger">
      This field is required.
    </small>
  </div>
</template>

<script>
import dropdownValues from "@/assets/data/dropdown-values/dropdown.json";
import surveyService from '@/services/surveyService';
import QuestionDisplay from '../utils/QuestionDisplay.vue';
import Multiselect from "vue-multiselect";

export default {
  // Register child components
  components: {
    QuestionDisplay,
    Multiselect
  },
  // Define props received from the parent component
  props: {
    question: Object, // Contains the question structure and fields
    required: Boolean, // Indicates if the question is mandatory
    showValidation: Boolean // Controls when to display validation messages
  },
  // Component's reactive data
  data() {
    return {
      inputValues: {}, // Stores the actual values for all dynamic input fields
      dropdownOptions: {}, // Stores options for dropdown and dropdown-search fields
      total: 0, // Used for calculations, e.g., sum of number inputs
      formattedMoneyValues: {} // Stores formatted strings for 'money-input' display
    };
  },
  // Computed properties for derived state
  computed: {
    // Determines if the entire form represented by this question is valid
    isFormValid() {
      // Check if all required fields have a non-empty/non-null value
      const allFieldsHaveValue = this.question.fields.every(field => {
        // 'display-number' fields are read-only and don't require user input
        if (field.type === 'display-number') return true;

        const value = this.inputValues[field.field_name];

        if (field.type === 'number-minutes-hour') {
          // For 'number-minutes-hour', check if 'value' property exists and is a non-negative number
          return value && typeof value.value === 'number' && value.value >= 0;
        }

        // For all other field types, check if the value is not undefined, null, or an empty string
        return value !== undefined && value !== null && value !== '';
      });

      // You can add more complex validation logic here if needed,
      // e.g., checking specific number ranges, email formats, etc.

      return allFieldsHaveValue;
    }
  },
  // Methods for component logic
  methods: {
    /**
     * Handles input from 'number' type fields.
     * Updates the specific input value, recalculates the total,
     * updates any 'display-number' fields, and triggers a general input update.
     * @param {Event} event - The input event
     * @param {Object} field - The field object from question.fields
     */
    updateTotal(event, field) {
      const value = Number(event.target.value);
      
      // Ensure number input is not negative or NaN; set to empty string for display clarity if invalid
      if (value < 0 || isNaN(value)) {
        this.inputValues[field.field_name] = '';
      } else {
        this.inputValues[field.field_name] = value;
      }

      // Recalculate the sum of all 'number' type inputs
      this.total = this.calculateTotal();

      // Update any 'display-number' fields with the new total
      this.question.fields.forEach(f => {
        if (f.type === 'display-number') {
          this.inputValues[f.field_name] = this.total;
        }
      });

      this.updateInput(); // Trigger the general update to save and emit
    },

    /**
     * Calculates the sum of all 'number' type fields.
     * @returns {number} The sum of all number input values.
     */
    calculateTotal() {
      return this.question.fields
        .filter(f => f.type === 'number') // Only consider fields of type 'number'
        .reduce((sum, f) => sum + Number(this.inputValues[f.field_name] || 0), 0); // Sum their values
    },

    /**
     * Handles input specifically for 'money-input' fields.
     * Formats the input for display (e.g., adds commas, handles 'K' for thousands)
     * and stores the raw numeric value.
     * @param {Event} event - The input event.
     * @param {string} fieldName - The name of the field being updated.
     */
    onMoneyInput(event, fieldName) {
      let raw = event.target.value.toUpperCase();

      // Remove invalid characters: only digits, commas, and trailing 'K'
      raw = raw.replace(/[^0-9K,]/g, ''); // Removes all characters that are not 0-9, K, or comma
      raw = raw.replace(/(K)(?=.)/g, ''); // Removes 'K' if it's not at the very end of the string

      const hasK = raw.endsWith('K'); // Check if 'K' suffix is present

      // Strip commas and 'K' to get the clean numeric part for parsing
      let numericPart = raw.replace(/,/g, '');
      if (hasK) {
        numericPart = numericPart.slice(0, -1); // Remove trailing 'K' for actual parsing
      }

      // Parse the numeric part into an integer; default to 0 if empty
      let parsedValue = numericPart ? parseInt(numericPart, 10) : 0;
      if (hasK) {
        parsedValue *= 1000; // Multiply by 1000 if 'K' suffix was present
      }

      // Store the parsed numeric value in inputValues (Vue 3 direct assignment)
      this.inputValues[fieldName] = parsedValue;

      // Format the numeric part with commas for display
      let formatted = numericPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      if (hasK) {
        formatted += 'K'; // Add 'K' back for display if it was originally there
      }

      // Update the formatted value for display in the input field (Vue 3 direct assignment)
      this.formattedMoneyValues[fieldName] = formatted;

      this.updateInput(); // Trigger the general update to save and emit
    },

    /**
     * General method to update the component's state and notify parent.
     * Saves the current inputValues to surveyService and emits 'input' and 'validation-status' events.
     */
    updateInput() {
      // Save the entire inputValues object for the current question ID
      surveyService.setAnswer(this.question.id, this.inputValues);
      // Emit the current inputValues to the parent component
      this.$emit("input", this.inputValues);
      // Emit the overall validation status of the form/question
      this.$emit('validation-status', this.isFormValid);
    },

    /**
     * Loads dropdown options from the external JSON file based on field configuration.
     */
    loadDropdownValues() {
      const options = {};
      this.question.fields?.forEach(field => {
        // If the field is a dropdown type and specifies a selection-list key
        if ((field.type === 'dropdown' || field.type === 'dropdown-search') && field['selection-list']) {
          const key = field['selection-list'].value; // Get the key for dropdown values
          options[field.field_name] = dropdownValues[key] || []; // Load options, default to empty array
        }
      });
      this.dropdownOptions = options; // Assign loaded options to data property
    },

    /**
     * Initializes inputValues and formattedMoneyValues for each field.
     * Ensures all fields have a default value on load or reset,
     * and correctly formats initial money values for display.
     */
    initializeFieldValues() {
      this.question.fields?.forEach(field => {
        // Initialize if the field's value is currently undefined or null in inputValues.
        // This prevents overwriting existing saved data.
        if (this.inputValues[field.field_name] === undefined || this.inputValues[field.field_name] === null) {
          if (field.type === 'number-minutes-hour') {
            // Default for number-minutes-hour fields
            this.inputValues[field.field_name] = { value: 0, unit: 'minutes' };
          } else if (field.type === 'number' || field.type === 'money-input' || field.type === 'display-number') {
            // Default for number and money fields
            this.inputValues[field.field_name] = 0;
          } else {
            // Default for other field types
            this.inputValues[field.field_name] = '';
          }
        }

        // Initialize formattedMoneyValues specifically for 'money-input' fields.
        // This ensures the displayed value is correctly formatted on load.
        if (field.type === 'money-input') {
            const numericValue = this.inputValues[field.field_name] || 0;
            // Format the numeric value with commas
            let formatted = String(numericValue).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            // Assign directly (Vue 3 syntax)
            this.formattedMoneyValues[field.field_name] = formatted;
        }
      });
    }
  },
  // Watcher to react to changes in the 'question' prop
  watch: {
    question: {
      immediate: true, // Run handler immediately when component mounts
      handler(newQuestion) {
        // When the question prop changes (e.g., navigating to a new question in a survey)
        // Load previously saved answers for the new question
        this.inputValues = surveyService.getAnswer(newQuestion.id) || {};
        // Initialize any new or uninitialized fields
        this.initializeFieldValues();
        // Load dropdown options specific to this question
        this.loadDropdownValues();
        // Emit the initial state and validation status after everything is loaded/initialized
        this.updateInput();
      }
    }
  },
  // Lifecycle hook: called after the component has been mounted to the DOM
  mounted() {
    // Initial setup when the component first mounts
    // Load saved answers
    this.inputValues = surveyService.getAnswer(this.question.id) || {};
    // Initialize field values
    this.initializeFieldValues();
    // Load dropdown options
    this.loadDropdownValues();
    // Emit the initial state and validation status
    this.updateInput();
  }
};
</script>

<style scoped>
/* Styles for read-only number displays */
.dis-number {
  background-color: #eaeaea;
}

/* Hide spin buttons for number inputs in WebKit browsers (Chrome, Safari) */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Hide spin buttons for number inputs in Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

/* Specific styling for the money input for better differentiation */
input[type="text"] {
   
  padding-right: 10px; /* Add some padding on the right for aesthetics */
}

/* Adjustments for form-inline elements */
.form-inline .i1 {
  width: 65%; /* Adjust width for value input */
  display: inline-block; /* Ensure it stays in line */
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.form-inline .i2 {
  width: 35%; /* Adjust width for unit dropdown */
  display: inline-block; /* Ensure it stays in line */
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: -1px; /* Overlap borders slightly */
}

/* Styles for radio buttons (curved-checkbox is a common custom style name) */
.curved-checkbox {
  display: flex; /* Use flexbox for alignment */
  align-items: center; /* Vertically align items */
  cursor: pointer; /* Indicate it's clickable */
  margin-bottom: 5px; /* Spacing between radio options */
}

.curved-checkbox input[type="radio"] {
  margin-right: 8px; /* Space between radio button and label text */
  /* Add custom styling for the radio button itself if needed */
}

.labelSpacing {
  margin-bottom: 1rem; /* Consistent spacing below each field group */
}
</style>